// covered-by: frontends/carrie/src/plugins/__tests__/rehype-memory-citation.test.ts

/**
 * Puts a saved memory's chip after the sentence it supports.
 *
 * warhol sends, with each memory the answer uses, the sentence it rests on
 * (`supportText`). This step finds that sentence in the rendered answer, wraps
 * it in `memory-support` runs so it can take a soft tint, and appends a
 * `memory-citation` chip after it.
 *
 * why a sibling of `rehype-claim-citation` and not another pass inside it: that
 * plugin matches CLAIM wording inside one text node and is already past the
 * 400-line bar; this one matches a whole SENTENCE that spans text nodes (bold
 * words, claim links) and places a chip after it. The two share nothing but the
 * tree, and this one runs second so it reads the claim badges already in place.
 *
 * why best-effort: nothing guarantees the sentence is on the page as sent. In
 * order, it is (1) found whole inside one block, (2) approximated by the block
 * that holds most of its words, which gets the chip at its end and no tint, or
 * (3) not placed at all. The rail still lists the memory in every case.
 *
 * why runs, not one wrapper: a sentence is often broken by `**bold**` or a claim
 * link, so its text nodes do not share a parent. Wrapping each text node that
 * the sentence touches keeps the tree valid and still tints as one sentence,
 * because every run carries the same memory key.
 */

import { CLAIM_CITATION_TAG } from "./rehype-claim-citation";

import { type LinkableMemory } from "../utils/memoryCitationFields";
import {
  MIN_WORD_SHARE,
  foldWithMap,
  supportNeedles,
  wordShare,
} from "../utils/memoryTextMatch";

/** The chip, after the sentence. */
export const MEMORY_CITATION_TAG = "memory-citation";

/** One run of the supported sentence. */
export const MEMORY_SUPPORT_TAG = "memory-support";

/**
 * The wrapper that keeps a chip, and the claim badges before it, on one line.
 *
 * why a class and not a custom tag: it holds plain content and needs no
 * component. The stylesheet gives it `white-space: nowrap` and a leading word
 * joiner (`.puntt-memory-cite-group`), so the last word of the sentence, its
 * badges and the chip wrap together and the chip never reads as the next
 * sentence's.
 */
export const MEMORY_CITE_GROUP_CLASS = "puntt-memory-cite-group";

interface HastNode {
  type: string;
  tagName?: string;
  value?: string;
  properties?: Record<string, unknown>;
  children?: HastNode[];
}

/** Blocks a sentence cannot run across. A table cell is its own. */
const BLOCK_TAGS = new Set([
  "p",
  "li",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "blockquote",
  "dt",
  "dd",
  "caption",
  "td",
  "th",
]);

/** Children a chip must not be appended after: the block's own nested blocks. */
const NESTED_BLOCK_TAGS = new Set([
  ...Array.from(BLOCK_TAGS),
  "ul",
  "ol",
  "table",
  "pre",
  "div",
  "hr",
]);

interface TextEntry {
  node: HastNode;
  parent: HastNode;
  /** A space standing for a `<br>`: counted for matching, never wrapped. */
  synthetic?: boolean;
}

interface Block {
  node: HastNode;
  texts: TextEntry[];
}

interface Collected {
  blocks: Block[];
  parentOf: Map<HastNode, HastNode>;
}

function collect(root: HastNode): Collected {
  const blocks: Block[] = [];
  const parentOf = new Map<HastNode, HastNode>();
  const walk = (node: HastNode, block: Block) => {
    for (const child of node.children ?? []) {
      parentOf.set(child, node);
      if (child.type === "text") {
        block.texts.push({ node: child, parent: node });
      } else if (child.type === "element") {
        // Code blocks are not prose: a sentence is never in one.
        if (child.tagName === "pre") continue;
        if (child.tagName === "br") {
          block.texts.push({
            node: { type: "text", value: " " },
            parent: node,
            synthetic: true,
          });
        } else if (child.tagName && BLOCK_TAGS.has(child.tagName)) {
          const inner: Block = { node: child, texts: [] };
          blocks.push(inner);
          walk(child, inner);
        } else {
          walk(child, block);
        }
      }
    }
  };
  const top: Block = { node: root, texts: [] };
  blocks.push(top);
  walk(root, top);
  return { blocks, parentOf };
}

const valueOf = (node: HastNode): string =>
  typeof node.value === "string" ? node.value : "";

const blockText = (block: Block): string =>
  block.texts.map((entry) => valueOf(entry.node)).join("");

const isBlank = (node: HastNode | undefined): boolean =>
  node?.type === "text" && valueOf(node).trim() === "";

function isGroup(node: HastNode | undefined): boolean {
  if (node?.type !== "element" || node.tagName !== "span") return false;
  const className = node.properties?.className;
  return (
    Array.isArray(className) && className.includes(MEMORY_CITE_GROUP_CLASS)
  );
}

/** A claim badge, an earlier memory chip or the group that holds one: what a chip tucks in behind. */
function isBadge(node: HastNode | undefined): boolean {
  if (node?.type !== "element") return false;
  if (node.tagName === MEMORY_CITATION_TAG || isGroup(node)) return true;
  return (
    node.tagName === CLAIM_CITATION_TAG && node.properties?.marker === true
  );
}

/** Where a chip goes after `siblings[index]`: past the badges that already follow it. */
function afterBadges(siblings: HastNode[], index: number): number {
  let pos = index + 1;
  for (;;) {
    let probe = pos;
    while (probe < siblings.length && isBlank(siblings[probe])) probe += 1;
    if (probe < siblings.length && isBadge(siblings[probe])) {
      pos = probe + 1;
      continue;
    }
    return pos;
  }
}

function chipNode(memory: LinkableMemory): HastNode {
  return {
    type: "element",
    tagName: MEMORY_CITATION_TAG,
    properties: {
      memoryId: memory.id,
      scope: memory.scope,
      number: memory.number,
    },
    children: [],
  };
}

/**
 * Put the chip after `siblings[index]`, past the badges that already follow it,
 * inside one no-wrap group with them.
 *
 * A second memory on the same sentence joins the first one's group, so two
 * chips stay together as well.
 */
function insertChip(
  siblings: HastNode[],
  index: number,
  memory: LinkableMemory,
): void {
  const pos = afterBadges(siblings, index);
  const before = siblings[pos - 1];
  if (pos - 1 > index && isGroup(before)) {
    before.children = [...(before.children ?? []), chipNode(memory)];
    return;
  }
  const group: HastNode = {
    type: "element",
    tagName: "span",
    properties: { className: [MEMORY_CITE_GROUP_CLASS] },
    children: [...siblings.slice(index + 1, pos), chipNode(memory)],
  };
  siblings.splice(index + 1, pos - index - 1, group);
}

/**
 * Wrap the part of every text node that lies inside `[start, end)` of the
 * block's text, and put the chip after the last one.
 */
function placeAfterSentence(
  found: Collected,
  block: Block,
  start: number,
  end: number,
  memory: LinkableMemory,
): void {
  let offset = 0;
  let part = 0;
  let last: { parent: HastNode; node: HastNode } | null = null;
  for (const entry of block.texts) {
    const text = valueOf(entry.node);
    const from = Math.max(start - offset, 0);
    const to = Math.min(end - offset, text.length);
    offset += text.length;
    if (entry.synthetic || from >= to) continue;
    const index = entry.parent.children?.indexOf(entry.node) ?? -1;
    if (index < 0 || !entry.parent.children) continue;
    const support: HastNode = {
      type: "element",
      tagName: MEMORY_SUPPORT_TAG,
      properties: { memoryId: memory.id, scope: memory.scope, part },
      children: [{ type: "text", value: text.slice(from, to) }],
    };
    part += 1;
    const pieces: HastNode[] = [];
    if (from > 0) pieces.push({ type: "text", value: text.slice(0, from) });
    pieces.push(support);
    if (to < text.length) {
      pieces.push({ type: "text", value: text.slice(to) });
    }
    entry.parent.children.splice(index, 1, ...pieces);
    last = { parent: entry.parent, node: support };
  }
  if (!last || memory.chip === false) return;

  // Climb out of inline formatting the sentence ends inside (`**not approval**`),
  // so the chip sits after the bold run, not inside it.
  let parent = last.parent;
  let anchor = last.node;
  for (;;) {
    const siblings = parent.children ?? [];
    const at = siblings.indexOf(anchor);
    const pos = afterBadges(siblings, at);
    const rest = siblings.slice(pos);
    const up = found.parentOf.get(parent);
    if (parent !== block.node && up && rest.every(isBlank)) {
      anchor = parent;
      parent = up;
      continue;
    }
    insertChip(siblings, at, memory);
    return;
  }
}

/** The chip at the end of a block's own text, before any nested list or table. */
function placeAtEndOfBlock(block: Block, memory: LinkableMemory): void {
  const children = block.node.children ?? [];
  let pos = children.length;
  while (
    pos > 0 &&
    children[pos - 1].type === "element" &&
    NESTED_BLOCK_TAGS.has(children[pos - 1].tagName ?? "")
  ) {
    pos -= 1;
  }
  block.node.children = children;
  insertChip(children, pos - 1, memory);
}

export interface MemoryCitationOptions {
  memories: LinkableMemory[];
  /**
   * Allow step (2): the chip at the end of the best-matching block when the
   * sentence is not found whole.
   *
   * why opt-in: an answer is drawn as several bodies (text segments, comment
   * sections), each its own render with no knowledge of the others. Whole-sentence
   * matching places a chip once, because a sentence is in one body; a fallback
   * would place one in EVERY body that holds most of the words. The caller that
   * knows it renders a single body turns this on.
   */
  fallback?: boolean;
}

export default function rehypeMemoryCitation(options: MemoryCitationOptions) {
  const memories = [...(options?.memories ?? [])].sort(
    (a, b) => a.number - b.number,
  );
  const fallback = options?.fallback === true;
  return (tree: HastNode) => {
    if (memories.length === 0) return;
    for (const memory of memories) {
      const needles = supportNeedles(memory.supportText);
      if (needles.length === 0) continue;
      // Collected anew per memory: an earlier one split text nodes and added runs.
      const found = collect(tree);

      const folds = found.blocks.map((block) => ({
        block,
        folded: foldWithMap(blockText(block)),
      }));
      let hit: { block: Block; start: number; end: number } | null = null;
      // Most literal form first, and the first block in document order that holds it.
      for (const needle of needles) {
        for (const { block, folded } of folds) {
          const at = folded.text.indexOf(needle);
          if (at < 0) continue;
          hit = {
            block,
            start: folded.origin[at],
            end: folded.origin[at + needle.length - 1] + 1,
          };
          break;
        }
        if (hit) break;
      }
      if (hit) {
        placeAfterSentence(found, hit.block, hit.start, hit.end, memory);
        continue;
      }

      // why not for a memory with no chip: the fallback places only a chip.
      if (!fallback || memory.chip === false) continue;
      let best: { block: Block; share: number } | null = null;
      for (const block of found.blocks) {
        if (block.node === tree) continue;
        const share = wordShare(memory.supportText, blockText(block));
        if (share >= MIN_WORD_SHARE && (!best || share > best.share)) {
          best = { block, share };
        }
      }
      if (best) placeAtEndOfBlock(best.block, memory);
    }
  };
}
